import { useState, useEffect, forwardRef, useCallback, useRef, useMemo } from "react";
import { Input } from "./input";
import type { ComponentProps, ChangeEvent } from "react";

export interface PhoneInputProps extends Omit<ComponentProps<typeof Input>, "type" | "onChange"> {
  /** Callback fired when the phone number value changes (receives raw digits only) */
  onChange?: (value: string) => void;
  /** The current phone number value (raw digits only, controlled) */
  value?: string;
  /** The default phone number value (raw digits only, uncontrolled) */
  defaultValue?: string;
  /** Format pattern where # represents a digit. Default: "(###) ###-####" */
  format?: string;
  /**
   * Allow international numbers to be entered. When the value starts with `+`, the `format` mask,
   * the leading-`1` trim, and the digit cap are all bypassed so the number is kept (and reported to
   * `onChange`) as `+` followed by its digits. Values without a leading `+` still use `format`.
   *
   * Off by default: the mask is US-only, so without this a `+44…` number is silently truncated to
   * 10 digits.
   */
  allowInternational?: boolean;
}

/**
 * A phone number input component with configurable formatting.
 *
 * Features:
 * - Configurable format patterns (default: "(###) ###-####")
 * - Real-time formatting as user types
 * - Automatic cursor positioning
 * - Filters non-numeric characters
 * - Returns raw digits (no formatting) via onChange callback
 * - Supports both controlled and uncontrolled usage
 *
 * @param onChange - Callback fired when the value changes (receives raw digits only)
 * @param value - The current phone number value for controlled usage (raw digits)
 * @param defaultValue - The default phone number value for uncontrolled usage (raw digits)
 * @param format - Format pattern where # represents a digit (default: "(###) ###-####")
 * @param ...props - All other Input component props (except type, onChange)
 *
 * @example
 * ```tsx
 * // Default US format
 * <PhoneInput
 *   onChange={(digits) => console.log('Phone:', digits)}
 * />
 *
 * // Custom international format
 * <PhoneInput
 *   format="+# (###) ###-####"
 *   value={phoneNumber}
 *   onChange={setPhoneNumber}
 * />
 * ```
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ onChange, value, defaultValue, format = "(###) ###-####", allowInternational, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    /**
     * Formats raw digits according to the format pattern.
     * Only uses as many digits as there are # placeholders in the format.
     */
    const formatPhoneNumber = useCallback(
      (digits: string): string => {
        if (!digits) return "";

        let formattedValue = "";
        let digitIndex = 0;

        // First pass: build formatted string with digits
        for (let i = 0; i < format.length; i++) {
          if (format[i] === "#") {
            if (digitIndex < digits.length) {
              formattedValue += digits[digitIndex];
              digitIndex++;
            } else {
              // No more digits to insert, stop processing
              break;
            }
          } else {
            // Add non-digit format characters
            formattedValue += format[i];
          }
        }

        // If all digits have been consumed, check if there are trailing non-digit characters
        // Count total # placeholders in format
        const totalPlaceholders = (format.match(/#/g) || []).length;

        if (digitIndex === digits.length && digitIndex === totalPlaceholders) {
          // All placeholders are filled, add any trailing characters
          for (let i = formattedValue.length; i < format.length; i++) {
            if (format[i] === "#") {
              break;
            }
            formattedValue += format[i];
          }
        }

        return formattedValue;
      },
      [format]
    );

    const shouldTrimLeadingOne = useMemo(
      () => !format.startsWith("+") && (format.match(/#/g) ?? []).length === 10,
      [format]
    );

    /**
     * Strips all non-numeric characters from input to get raw digits.
     */
    const stripFormatting = useCallback(
      (formatted: string): string => {
        const unformatted = formatted.replace(/\D/g, "");
        if (shouldTrimLeadingOne && unformatted.startsWith("1")) {
          return unformatted.slice(1);
        }
        return unformatted;
      },
      [shouldTrimLeadingOne]
    );

    const isInternational = useCallback(
      (raw: string) => !!allowInternational && raw.trimStart().startsWith("+"),
      [allowInternational]
    );

    const toDisplayValue = useCallback(
      (raw: string) => (isInternational(raw) ? `+${raw.replace(/\D/g, "")}` : formatPhoneNumber(stripFormatting(raw))),
      [isInternational, formatPhoneNumber, stripFormatting]
    );

    /**
     * Initialize display value based on controlled/uncontrolled usage.
     */
    const [displayValue, setDisplayValue] = useState<string>(() => {
      if (value !== undefined) {
        return toDisplayValue(value);
      }
      if (defaultValue !== undefined) {
        return toDisplayValue(defaultValue);
      }
      return "";
    });

    /**
     * Sync display value when controlled value changes.
     *
     * TODO: clean up bad effect in migration to design-system
     */
    useEffect(() => {
      if (value !== undefined) {
        setDisplayValue(toDisplayValue(value));
      }
    }, [value, toDisplayValue]);

    /**
     * Handle input changes: extract digits, format, and notify parent with raw digits.
     */
    const handleInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement> | string) => {
        const inputValue = typeof e === "string" ? e : e.target.value;
        const cursorPosition = typeof e === "string" ? inputValue.length : e.target.selectionStart ?? 0;

        if (isInternational(inputValue)) {
          const international = `+${inputValue.replace(/\D/g, "")}`;
          setDisplayValue(international);
          onChange?.(international);
          return;
        }

        // Extract only digits from the input
        const allDigits = stripFormatting(inputValue);

        // Count total # placeholders in format to determine max digits allowed
        const maxDigits = (format.match(/#/g) || []).length;

        // Limit digits to the number of placeholders in the format
        const digits = allDigits.slice(0, maxDigits);

        // Count how many digits were before the cursor
        const digitsBeforeCursor = stripFormatting(inputValue.slice(0, cursorPosition)).length;

        // Format the digits
        const formatted = formatPhoneNumber(digits);

        // Update display value
        setDisplayValue(formatted);

        // Calculate new cursor position by finding the Nth digit in formatted string
        let newCursorPosition = 0;
        let digitCount = 0;
        for (let i = 0; i < formatted.length; i++) {
          const ch = formatted[i];
          if (ch !== undefined && /\d/.test(ch)) {
            digitCount++;
            if (digitCount === digitsBeforeCursor) {
              newCursorPosition = i + 1;
              break;
            }
          }
        }

        // If we've counted all digits, place cursor at end
        if (digitCount < digitsBeforeCursor || digitsBeforeCursor === digits.length) {
          newCursorPosition = formatted.length;
        }

        // Set cursor position after React updates the input
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
          }
        });

        // Call onChange with raw digits only (limited to format's max digits)
        if (onChange) {
          onChange(digits);
        }
      },
      [stripFormatting, formatPhoneNumber, onChange, format, isInternational]
    );

    return (
      <Input
        {...props}
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        onKeyDown={(e) => {
          if (isInternational(displayValue)) return;
          if (e.key === "Backspace" && /\D/g.test(displayValue.at(-1) ?? "")) {
            const digits = stripFormatting(displayValue);
            handleInputChange(digits.slice(0, digits.length - 1));
            e.preventDefault();
          }
        }}
        type="tel"
        value={displayValue}
        onChange={handleInputChange}
      />
    );
  }
);

PhoneInput.displayName = "PhoneInput";
